import { useState } from "react";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Check, Sparkles, Loader2, Mic, Infinity as InfinityIcon, LineChart } from "lucide-react";
import { toast } from "sonner";

const IMG = "https://images.unsplash.com/photo-1744060204728-f68e434a3edf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxydW5uZXIlMjBzdW5yaXNlfGVufDB8fHx8MTc4NDc0NzM4Nnww&ixlib=rb-4.1.0&q=85";

const FEATURES = [
  { icon: Mic, text: "Unlimited voice & text logging" },
  { icon: InfinityIcon, text: "Full unlimited history" },
  { icon: LineChart, text: "Trends & insights over time" },
  { icon: Sparkles, text: "Priority AI sugar estimates" },
];

export default function Pricing() {
  const { user } = useAuth();
  const [plan, setPlan] = useState("premium_monthly");
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: plan, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-8 max-w-xl mx-auto">
        <div className="relative rounded-2xl overflow-hidden fade-up">
          <img src={IMG} alt="Runner at sunrise" className="w-full h-44 object-cover" />
          <div className="absolute inset-0 bg-foreground/50 flex flex-col justify-end p-6">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-white/80">SugarBurn Premium</p>
            <h1 className="font-heading text-3xl font-black text-white">Go further. Track it all.</h1>
          </div>
        </div>

        {user?.is_premium ? (
          <div className="bg-secondary/10 border border-secondary rounded-2xl p-8 text-center fade-up" data-testid="already-premium">
            <Sparkles className="w-8 h-8 mx-auto mb-3 text-secondary" />
            <h2 className="font-heading text-2xl font-black">You're Premium 🎉</h2>
            <p className="text-muted-foreground mt-1">Thanks for supporting SugarBurn. Enjoy unlimited everything.</p>
          </div>
        ) : (
          <div className="bg-card border border-border rounded-2xl p-8 fade-up">
            <div className="flex gap-2 p-1 bg-muted rounded-full mb-6">
              <button
                onClick={() => setPlan("premium_monthly")}
                className={`flex-1 rounded-full py-2.5 text-sm font-bold transition-colors ${plan === "premium_monthly" ? "bg-card shadow text-foreground" : "text-muted-foreground"}`}
                data-testid="plan-monthly-btn"
              >
                Monthly · $4.99
              </button>
              <button
                onClick={() => setPlan("premium_yearly")}
                className={`flex-1 rounded-full py-2.5 text-sm font-bold transition-colors ${plan === "premium_yearly" ? "bg-card shadow text-foreground" : "text-muted-foreground"}`}
                data-testid="plan-yearly-btn"
              >
                Yearly · $49.99
              </button>
            </div>

            <ul className="space-y-3 mb-8">
              {FEATURES.map((f, i) => {
                const Icon = f.icon;
                return (
                  <li key={i} className="flex items-center gap-3">
                    <span className="w-8 h-8 rounded-lg bg-secondary/15 flex items-center justify-center shrink-0">
                      <Icon className="w-4 h-4 text-secondary" />
                    </span>
                    <span className="font-medium">{f.text}</span>
                    <Check className="w-4 h-4 text-secondary ml-auto" />
                  </li>
                );
              })}
            </ul>

            <Button onClick={checkout} disabled={loading} className="w-full rounded-full h-12 text-base font-bold" data-testid="checkout-btn">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : `Upgrade — ${plan === "premium_monthly" ? "$4.99/mo" : "$49.99/yr"}`}
            </Button>
            <p className="text-xs text-muted-foreground text-center mt-3">Secure checkout via Stripe. Cancel anytime.</p>
          </div>
        )}
      </div>
    </Layout>
  );
}
